import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLoginRedirectPath, loginWithEmail } from '@/lib/auth';

export default function ClientLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const result = await loginWithEmail(email, password);
      if (!result.success) {
        setError(result.error);
        return;
      }
      navigate(getLoginRedirectPath(result.userType), { replace: true });
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3] flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-[420px]">
          {/* Back button */}
          <a
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-1.5 text-[#888888] text-[13px] no-underline hover:text-[#F05A00] transition-colors cursor-pointer mb-8"
          >
            <i className="ri-arrow-left-line"></i>
            Back
          </a>

          {/* Logo block */}
          <div className="flex flex-col items-center mb-0">
            <img
              src="https://qsenozfawbynwkmpxyjr.supabase.co/storage/v1/object/public/Logo/Logo_portal.png"
              alt="JPH Portal"
              className="max-w-[220px] w-full object-contain"
            />
            <div className="w-10 h-[2px] bg-[#F05A00] mt-4 mb-5"></div>
            {/* Role badge */}
            <span
              className="text-[11px] font-bold tracking-[2px] px-4 py-[5px] rounded-full text-white whitespace-nowrap"
              style={{ background: '#F05A00', letterSpacing: '2px' }}
            >
              CLIENT PORTAL
            </span>
          </div>

          {/* Title */}
          <h1 className="text-[22px] font-bold text-[#1A1A1A] mt-7 mb-1">Client Portal Login</h1>
          <p className="text-[13px] text-[#999999] mb-7">Sign in to your client account</p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[13px] font-semibold text-[#555555] mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                className="w-full bg-white border-[1.5px] border-[#E0DDD8] rounded-[10px] px-4 py-[14px] text-[14px] text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none transition-all"
                style={{ boxShadow: 'none' }}
                onFocus={(e) => { e.target.style.boxShadow = '0 0 0 3px rgba(240,90,0,0.1)'; e.target.style.borderColor = '#F05A00'; }}
                onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = '#E0DDD8'; }}
                placeholder="you@japanhomes.co.nz"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label className="block text-[13px] font-semibold text-[#555555] mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  className={`w-full bg-white border-[1.5px] rounded-[10px] px-4 py-[14px] text-[14px] text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none transition-all pr-12 ${error ? 'border-red-400' : 'border-[#E0DDD8]'}`}
                  style={{ boxShadow: 'none' }}
                  onFocus={(e) => { e.target.style.boxShadow = '0 0 0 3px rgba(240,90,0,0.1)'; e.target.style.borderColor = '#F05A00'; }}
                  onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = error ? '#f87171' : '#E0DDD8'; }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#AAAAAA] hover:text-[#555555] cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                </button>
              </div>
              {error && <p className="text-red-500 text-xs mt-1.5">{error}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full text-white py-[15px] rounded-[10px] text-[15px] font-bold hover:bg-[#D94F00] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap mt-1"
              style={{ background: '#F05A00', boxShadow: '0 4px 14px rgba(240,90,0,0.25)' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Signing in...
                </span>
              ) : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-[#BBBBBB] text-[12px] mt-5">
            Access issues? Contact your system administrator.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="py-5 text-center">
        <p className="text-[#CCCCCC] text-[11px]">Produced by Japan Homes Group · New Zealand</p>
      </div>
    </div>
  );
}
